import React, { FC, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Input, Spin, Empty } from "antd";
import { Icon } from "Components/UI";
import Button from "Components/UI/Button";
import { ActivitiesActions } from "Store/Activities/Activities.actions";
import { IGlobalReducerState } from "Store/Base/interface/IGlobalReducerState";
import { ITopicDnDResponse } from "Data/interfaces/Activities/IDragAndDropDataFormat";
import ActivityCard from "./ActivityCard";
import styles from "./ActivitiesList.module.scss";

interface IActivitiesList {
  constructionSiteId: number;
  onCreateActivity: () => void;
  onOpenActivity: (activityId: string) => void;
}

const ActivitiesList: FC<IActivitiesList> = ({
  constructionSiteId,
  onCreateActivity,
  onOpenActivity,
}) => {
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState("");

  const { listActivities, isLoading } = useSelector((state: IGlobalReducerState) => ({
    listActivities: state.activities.listActivities,
    isLoading: state.activities.isLoading,
  }));

  useEffect(() => {
    // Load activities when component mounts
    dispatch(ActivitiesActions.listActivities({
      csId: constructionSiteId,
    }));
  }, [dispatch, constructionSiteId]);

  const activities = useMemo(() => {
    if (!listActivities?.activities) {
      return [];
    }

    // Convert activities object to array
    return Object.values(listActivities.activities) as ITopicDnDResponse[];
  }, [listActivities]);

  const filteredActivities = useMemo(() => {
    if (!searchTerm) {
      return activities;
    }

    const searchLower = searchTerm.toLowerCase();
    return activities.filter((activity) =>
      activity.Title.toLowerCase().includes(searchLower) ||
      activity.Identifier.toLowerCase().includes(searchLower) ||
      activity.Description?.toLowerCase().includes(searchLower)
    );
  }, [activities, searchTerm]);

  const handleRefresh = () => {
    dispatch(ActivitiesActions.listActivities({
      csId: constructionSiteId,
    }));
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div className={styles.headerTop}>
          <h3 className={styles.title}>Atividades</h3>
          <div className={styles.actions}>
            <button
              className={styles.refreshButton}
              onClick={handleRefresh}
              title="Atualizar"
            >
              <Icon icon="atualizar" customSize={16} />
            </button>
          </div>
        </div>

        <Button
          type="primary"
          width="100%"
          height={36}
          onClick={onCreateActivity}
          className={styles.createButton}
        >
          <Icon icon="adicionarAlt" customSize={16} color="branco" />
          <span>Nova Atividade</span>
        </Button>

        <div className={styles.searchContainer}>
          <Input
            placeholder="Buscar atividade..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            prefix={<Icon icon="buscar" customSize={14} />}
            allowClear
          />
        </div>
      </div>

      <div className={styles.listContainer}>
        {isLoading ? (
          <div className={styles.loadingContainer}>
            <Spin size="large" />
          </div>
        ) : filteredActivities.length === 0 ? (
          <Empty
            description={
              searchTerm
                ? "Nenhuma atividade encontrada"
                : "Nenhuma atividade criada"
            }
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : (
          <div className={styles.list}>
            {filteredActivities.map((activity) => (
              <ActivityCard
                key={activity.Guid}
                activity={activity}
                searchTerm={searchTerm}
                onClick={() => onOpenActivity(activity.Guid)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivitiesList;
